import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import {
  FolderKanban, ClipboardCheck, CheckCircle2, Clock, Loader2, ArrowRight,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <Card className="border-slate-200 bg-white p-5 shadow-sm">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-md ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-heading text-3xl font-black text-slate-900">{value}</div>
      <div className="overline mt-1 text-slate-500">{label}</div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/projects").then((r) => setProjects(r.data.slice(0, 6)));
  }, []);

  if (!stats)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <p className="overline text-blue-600">{user.role_label}</p>
        <h1 className="font-heading text-3xl font-black text-slate-900 sm:text-4xl">
          {t("dashboard.welcome", { name: user.name.split(" ")[0] })}
        </h1>
        <p className="mt-1 text-slate-500">{t("dashboard.overview")}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={FolderKanban} label={t("dashboard.projects")} value={stats.total_projects} tint="bg-blue-100 text-blue-600" />
        <StatCard icon={ClipboardCheck} label={user.role === "technician" ? t("dashboard.myInstalls") : t("dashboard.installations")} value={stats.total_installations} tint="bg-indigo-100 text-indigo-600" />
        <StatCard icon={Clock} label={t("dashboard.underReview")} value={stats.pending_review} tint="bg-amber-100 text-amber-600" />
        <StatCard icon={CheckCircle2} label={t("dashboard.approved")} value={stats.approved} tint="bg-emerald-100 text-emerald-600" />
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-slate-900">{t("dashboard.recentProjects")}</h2>
          <button onClick={() => navigate("/projects")} className="flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="view-all-projects">
            {t("common.viewAll")} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="grid gap-3">
          {projects.length === 0 && <p className="text-sm text-slate-400">{t("dashboard.noProjects")}</p>}
          {projects.map((p) => (
            <Card
              key={p.id}
              data-testid={`dash-project-${p.id}`}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="flex cursor-pointer items-center justify-between border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-blue-400"
            >
              <div>
                <div className="font-heading font-bold text-slate-900">{p.name || p.id}</div>
                <div className="text-sm text-slate-500">{p.company_name} · {t("dashboard.busesCount", { count: p.total_buses })}</div>
              </div>
              <StatusBadge status={p.status} />
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
